import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ThumbsDown, ThumbsUp, TriangleAlert } from "lucide-react";
import { READING_COVERAGE, REVISION_CHANGE_STATUS, TASK_STATUS, formatTimestamp } from "@brainlog/shared";
import { ApiError, errorMessage, get, post, put } from "@/lib/api";
import { qk, useMe, useTree, useWorkspace } from "@/lib/queries";
import { openNote, openPaper, openView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DiffView } from "@/components/DiffView";
import { MarkdownPreview } from "@/features/notes/MarkdownPreview";
import type { Proposal } from "./types";
import { SourceList } from "./RunFlow";

type Mode = "insert" | "replace_selection" | "new_note";
/** Plain names for the kinds of issue "Review draft" reports, and for requirement checks. */
const ISSUE_TYPES: Record<string, string> = { structure: "Structure", argument: "Argument", unsupported_claim: "Claim without support", missing_information: "Missing information", clarity: "Clarity" };
const REQUIREMENT_STATUS: Record<string, string> = { met: "Met", missing: "Missing", unknown: "Not known" };

interface ApplyItem {
  key: string;
  edits?: Record<string, unknown>;
}

function FolderSelect({ workspaceId, value, onChange }: { workspaceId: string; value: string; onChange(v: string): void }) {
  const tree = useTree(workspaceId);
  const opts = useMemo(() => {
    const list = tree.data?.folders ?? [];
    const out: { id: string; label: string }[] = [];
    const walk = (p: string | null, prefix: string) => {
      for (const f of list.filter((x) => x.parentId === p)) {
        out.push({ id: f.id, label: prefix + f.name });
        walk(f.id, `${prefix}${f.name} / `);
      }
    };
    walk(null, "");
    return out;
  }, [tree.data]);
  return (
    <div className="space-y-1">
      <Label htmlFor="ai-folder">Save into folder</Label>
      <select id="ai-folder" className="h-8 w-full rounded-md border bg-background px-2 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Top level (no folder)</option>
        {opts.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Renders [S#] markers as clickable source references. */
function WithRefs({ text, index }: { text: string; index: Record<string, any> }) {
  const parts = text.split(/(\[S\d+(?:\s*,\s*S\d+)*\])/g);
  return (
    <>
      {parts.map((p, i) => {
        const m = /^\[(S\d+(?:\s*,\s*S\d+)*)\]$/.exec(p);
        if (!m) return <span key={i}>{p}</span>;
        return (
          <span key={i}>
            [
            {m[1].split(/\s*,\s*/).map((ref, j) => {
              const s = index[ref];
              return (
                <span key={ref}>
                  {j > 0 && ", "}
                  {s ? (
                    <button
                      type="button"
                      className="underline"
                      title={s.title}
                      onClick={() => (s.type === "paper_page" || s.type === "library_item" ? openPaper(s.id, s.title, s.page ?? undefined) : s.type === "note" || s.type === "selection" ? openNote(s.id, s.title) : undefined)}
                    >
                      {ref}
                    </button>
                  ) : (
                    ref
                  )}
                </span>
              );
            })}
            ]
          </span>
        );
      })}
    </>
  );
}

export function ProposalReview({ proposal: p, applications }: { proposal: Proposal; applications: { itemKey: string; entityType: string; entityId: string | null }[] }) {
  const qc = useQueryClient();
  const me = useMe();
  const r = p.result;
  const items: any[] = r.items ?? [];
  const index: Record<string, any> = Object.fromEntries((p.inputSources ?? []).map((s) => [s.ref, s]));
  const applied = new Map(applications.map((a) => [a.itemKey, a]));
  const [stale, setStale] = useState<null | { inputText: string | null; currentText: string; retry(mode: Mode, confirm: boolean): void }>(null);
  const [busy, setBusy] = useState(false);
  const [folderId, setFolderId] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(items.filter((i) => !i.likelyDuplicateOf && !i.duplicate && !i.exists && !applied.has(i.key)).map((i) => i.key)));
  const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>({});
  const ws = useWorkspace(p.workspaceId);
  const members = ws.data?.members ?? [];
  const edit = (key: string, field: string, value: unknown) => setEdits((e) => ({ ...e, [key]: { ...(e[key] ?? {}), [field]: value } }));
  const val = (item: any, field: string) => (edits[item.key]?.[field] !== undefined ? edits[item.key][field] : item[field]);
  const toggle = (key: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(key)) n.delete(key);
    else n.add(key);
    return n;
  });

  const apply = async (applyItems: ApplyItem[], mode: Mode = "new_note", confirmStale = false) => {
    setBusy(true);
    try {
      const res = await post<{ applied: { key: string; entityType: string; entityId: string | null; duplicate: boolean }[] }>(`/api/ai/proposals/${p.id}/apply`, {
        items: applyItems.map((i) => ({ key: i.key, edits: i.edits ?? edits[i.key] ?? {} })),
        mode,
        folderId: folderId || null,
        confirmStale,
      });
      setStale(null);
      const created = res.applied.filter((a) => !a.duplicate);
      const dups = res.applied.filter((a) => a.duplicate);
      toast.success(`${created.length ? `Applied ${created.length} item(s).` : ""}${dups.length ? ` ${dups.length} item(s) were already applied — nothing duplicated.` : ""}`.trim() || "Done.");
      qc.invalidateQueries({ queryKey: ["proposal", p.id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: qk.tree(p.workspaceId) });
      if (p.targetNoteId) {
        qc.invalidateQueries({ queryKey: qk.note(p.targetNoteId) });
        qc.invalidateQueries({ queryKey: ["comments", p.targetNoteId] });
      }
      if (p.targetManuscriptId) qc.invalidateQueries({ queryKey: ["manuscript", p.targetManuscriptId] });
      const first = created[0];
      // Project set-up creates many things at once: stay on the checklist instead of jumping to one note.
      if (p.action !== "project_setup" && first?.entityType === "note" && first.entityId) openNote(first.entityId, "Note", p.workspaceId);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.details?.stale) {
        setStale({ inputText: e.details.inputText, currentText: e.details.currentText, retry: (m, c) => void apply(applyItems, m, c) });
      } else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const selItems = () => [...selected].map((key) => ({ key }));
  const flags = p.validation?.flags ?? [];
  const warnings = p.validation?.warnings ?? [];

  const header = (
    <div className="space-y-2">
      {p.isDemo && (
        <p className="rounded-md border border-ai/40 bg-ai-bg px-2 py-1 text-xs">
          <strong>Practice mode.</strong> Made by simple rules from your text, not by AI. It does not understand or check anything.
        </p>
      )}
      {flags.length > 0 && (
        <div className="rounded-md border border-warning/50 bg-warning/10 p-2 text-xs" role="status">
          <p className="mb-1 flex items-center gap-1 font-medium">
            <TriangleAlert className="size-3.5" aria-hidden /> Points to check
          </p>
          <ul className="list-disc space-y-0.5 pl-4">
            {flags.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.map((w, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          {w}
        </p>
      ))}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">What the AI read ({p.inputSources?.length ?? 0} sources)</summary>
        <div className="mt-1">
          <SourceList sources={p.inputSources ?? []} />
        </div>
      </details>
    </div>
  );

  const staleBox = stale && (
    <div className="space-y-2 rounded-md border border-warning/60 p-2 text-sm" role="alert">
      <p className="font-medium">The note changed after the assistant read it.</p>
      <p className="text-xs">Review the difference. Replacing text is not allowed for a changed note; you can add the result at the end or save it as a new note.</p>
      {stale.inputText !== null && <DiffView before={stale.inputText} after={stale.currentText} beforeLabel="What the assistant read" afterLabel="Current note" />}
      <div className="flex flex-wrap gap-2">
        <Button size="xs" onClick={() => stale.retry("insert", true)}>
          Add at the end anyway
        </Button>
        <Button size="xs" variant="outline" onClick={() => stale.retry("new_note", false)}>
          Save as a new note instead
        </Button>
        <Button size="xs" variant="ghost" onClick={() => setStale(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );

  const appliedNote = (key: string) => {
    const a = applied.get(key);
    if (!a) return null;
    return <Badge variant="outline">Applied{a.entityType === "task" ? " — task created" : ""}</Badge>;
  };

  let body: React.ReactNode = null;
  switch (p.action) {
    case "summarize": {
      const it = items[0];
      const sel = p.inputVersion?.selection;
      body = (
        <div className="space-y-2">
          <Label htmlFor="sum-text">Summary (editable)</Label>
          <Textarea id="sum-text" rows={6} value={String(val(it, "text"))} onChange={(e) => edit(it.key, "text", e.target.value)} />
          {it.keyPoints?.length > 0 && (
            <ul className="list-disc pl-5 text-sm">
              {it.keyPoints.map((k: string, i: number) => (
                <li key={i}>{k}</li>
              ))}
            </ul>
          )}
          {appliedNote(it.key)}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "insert")}>
              Insert into note
            </Button>
            {sel && (
              <Button size="sm" variant="outline" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "replace_selection")}>
                Replace the selected text
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "new_note")}>
              Save as new note
            </Button>
          </div>
        </div>
      );
      break;
    }
    case "extract_tasks":
    case "next_steps": {
      body = (
        <div className="space-y-2">
          {!items.length && <p className="text-sm text-muted-foreground">No actionable items found.</p>}
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.key} className="space-y-1.5 rounded-md border bg-background p-2 text-sm">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={selected.has(it.key)} disabled={applied.has(it.key)} onChange={() => toggle(it.key)} aria-label={`Include ${it.title}`} />
                  <Input value={String(val(it, "title"))} onChange={(e) => edit(it.key, "title", e.target.value)} className="h-8" aria-label="Task title" />
                </label>
                {appliedNote(it.key)}
                {(it.likelyDuplicateOf || it.duplicate) && (
                  <p className="text-xs font-medium">
                    Likely duplicate of an existing task{it.likelyDuplicateOf ? `: “${it.likelyDuplicateOf.title}” (${it.likelyDuplicateOf.status.replace("_", " ")})` : ""}.
                  </p>
                )}
                {p.action === "extract_tasks" ? (
                  <>
                    <blockquote className="border-l-2 pl-2 text-xs italic">
                      {it.sourcePassage} {!it.passageFound && <span className="not-italic">(this passage was not found in the note)</span>}
                    </blockquote>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs" htmlFor={`as-${it.key}`}>
                          Assignee
                        </Label>
                        <select
                          id={`as-${it.key}`}
                          className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                          value={String(val(it, "assigneeId") ?? "")}
                          onChange={(e) => edit(it.key, "assigneeId", e.target.value || null)}
                        >
                          <option value="">Unassigned</option>
                          {members.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.displayName}
                            </option>
                          ))}
                        </select>
                        {it.assigneeNote && <p className="text-xs text-muted-foreground">{it.assigneeNote}</p>}
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-xs" htmlFor={`due-${it.key}`}>
                          Due date
                        </Label>
                        <Input id={`due-${it.key}`} type="date" className="h-8" value={String(val(it, "dueDate") ?? "")} onChange={(e) => edit(it.key, "dueDate", e.target.value || null)} />
                        {it.dueNote && <p className="text-xs text-muted-foreground">{it.dueNote}</p>}
                        {it.suggestedDate && (
                          <div className="text-xs">
                            <Button size="xs" variant="outline" onClick={() => edit(it.key, "dueDate", it.suggestedDate.date)}>
                              Use suggested {it.suggestedDate.date}
                            </Button>
                            <p className="text-muted-foreground">
                              {it.suggestedDate.label}. {it.suggestedDate.explanation}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs">Why: {it.reason}</p>
                    {it.refs?.length > 0 && (
                      <p className="text-xs">
                        Based on: <WithRefs text={`[${it.refs.join(", ")}]`} index={index} />
                      </p>
                    )}
                  </>
                )}
                <div className="space-y-0.5">
                  <Label className="text-xs" htmlFor={`st-${it.key}`}>
                    Status
                  </Label>
                  <select
                    id={`st-${it.key}`}
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                    value={String(val(it, "status") ?? (p.action === "next_steps" ? "backlog" : "todo"))}
                    onChange={(e) => edit(it.key, "status", e.target.value)}
                  >
                    {TASK_STATUS.values.map((s) => (
                      <option key={s} value={s}>
                        {TASK_STATUS.labels[s]}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            ))}
          </ul>
          {items.length > 0 && (
            <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
              Add {selected.size} selected task(s) to the backlog
            </Button>
          )}
        </div>
      );
      break;
    }
    case "explore_topic": {
      const it = items[0];
      body = (
        <div className="space-y-2 text-sm">
          <Badge variant="outline">{r.label}</Badge>
          <h4 className="font-medium">Angles</h4>
          <ul className="list-disc space-y-1 pl-5">
            {it.angles.map((a: any) => (
              <li key={a.key}>
                <strong>{a.title}</strong> — {a.description} {a.refs.length > 0 && <WithRefs text={`[${a.refs.join(", ")}]`} index={index} />}
              </li>
            ))}
          </ul>
          <h4 className="font-medium">Questions</h4>
          <ul className="list-disc pl-5">
            {it.questions.map((q: string, i: number) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
          <h4 className="font-medium">Outline</h4>
          <ul className="list-disc pl-5">
            {it.outline.map((o: any, i: number) => (
              <li key={i}>
                {o.heading}
                <ul className="list-[circle] pl-5">
                  {o.points.map((pt: string, j: number) => (
                    <li key={j}>{pt}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {appliedNote(it.key)}
          <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "new_note")}>
              Save as new note
            </Button>
            {p.targetNoteId && (
              <Button size="sm" variant="outline" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "insert")}>
                Add to the current note
              </Button>
            )}
          </div>
        </div>
      );
      break;
    }
    case "recent_updates": {
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">
            Searched the web for “{r.searchMeta?.query}”{r.searchMeta?.retrievedAt ? ` on ${formatTimestamp(r.searchMeta.retrievedAt, me.data?.timezone)}` : ""}. Publication dates come from the search results;
            event dates appear only when the text gives them.
          </p>
          {!items.length && <p className="text-muted-foreground">No usable results.</p>}
          <ul className="space-y-2">
            {items.map((u) => (
              <li key={u.key} className="rounded-md border bg-background p-2">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={selected.has(u.key)} onChange={() => toggle(u.key)} />
                  <span>
                    <strong>{u.headline}</strong>
                    <span className="block text-xs">{u.summary}</span>
                    <a href={u.sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="block break-all text-xs underline">
                      {u.sourceTitle}
                    </a>
                    <span className="block text-xs text-muted-foreground">
                      Published: {u.publishedDate ?? "not reported"} · Event date: {u.eventDate ?? "not stated"}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {items.length > 0 && (
            <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
              Save selected as a note
            </Button>
          )}
        </div>
      );
      break;
    }
    case "deep_read": {
      const it = items[0];
      const done = it ? applied.get(it.key) : undefined;
      body = it ? (
        <div className="space-y-2 text-sm">
          <p>
            <Badge variant="outline">{(READING_COVERAGE.labels as Record<string, string>)[r.coverage] ?? r.coverage}</Badge> <span className="text-xs">{r.coverageDetail}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {it.findings.length} findings · {it.claims.length} claims with {it.claims.reduce((n: number, c: any) => n + c.evidence.length, 0)} verified quotations · {it.limitations.length} limitations ·{" "}
            {it.referencesToCheck.length} references to check
          </p>
          {done?.entityId ? (
            <Button size="sm" onClick={() => openNote(done.entityId!, "In-depth note", p.workspaceId)}>
              Open the in-depth note
            </Button>
          ) : (
            <>
              {p.error && <p className="text-xs text-destructive">{p.error}</p>}
              <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
              <Button size="sm" disabled={busy} onClick={() => apply([{ key: it.key }])}>
                Create the in-depth note
              </Button>
            </>
          )}
        </div>
      ) : null;
      break;
    }
    case "glossary": {
      const saved = applications.filter((x) => x.itemKey.startsWith("glossary:") && x.entityId);
      body = (
        <div className="space-y-2 text-sm">
          {items.length ? (
            <ul className="space-y-1.5" aria-label="Proposed terms">
              {items.map((i) => (
                <li key={i.key} className="flex items-start gap-2">
                  <Checkbox checked={selected.has(i.key)} onCheckedChange={() => toggle(i.key)} className="mt-0.5" aria-label={`Keep ${i.term}`} />
                  <div className="min-w-0 flex-1">
                    <p>
                      <span className="font-medium">{i.term}</span>: {i.definition}
                    </p>
                    {i.avoid.length > 0 && <p className="text-xs text-muted-foreground">Avoid: {i.avoid.join(", ")}</p>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">No new term was found.</p>
          )}
          <div className="flex flex-wrap gap-2">
            {items.length > 0 && (
              <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
                Add {selected.size} term{selected.size === 1 ? "" : "s"} to the glossary
              </Button>
            )}
            {saved.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => openNote(saved.at(-1)!.entityId!, "Glossary", p.workspaceId)}>
                Open the glossary
              </Button>
            )}
          </div>
        </div>
      );
      break;
    }
    case "questionnaire": {
      const it = items[0];
      const done = it ? applied.get(it.key) : undefined;
      body = it ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium">{it.title}</p>
          <p className="text-xs text-muted-foreground">{it.purpose}</p>
          <ul className="list-disc space-y-0.5 pl-5 text-xs">
            {it.themes.map((t: any) => (
              <li key={t.heading}>
                {t.heading} ({t.questions.length} question{t.questions.length === 1 ? "" : "s"})
              </li>
            ))}
          </ul>
          {done?.entityId ? (
            <Button size="sm" onClick={() => openNote(done.entityId!, it.title || "Questionnaire", p.workspaceId)}>
              Open the questionnaire
            </Button>
          ) : (
            <>
              <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
              <Button size="sm" disabled={busy} onClick={() => apply([{ key: it.key }])}>
                Save the questionnaire as a note
              </Button>
            </>
          )}
        </div>
      ) : null;
      break;
    }
    case "learn_step":
    case "grill_step": {
      body = (
        <div className="space-y-2 text-sm">
          <p>{p.action === "learn_step" ? "The lesson is ready." : "The questions are ready."}</p>
          <Button size="sm" onClick={() => openView("learning", p.workspaceId, p.action === "learn_step" ? "Learning" : "Plan review", undefined, (p.params as { sessionId: string }).sessionId)}>
            Open
          </Button>
        </div>
      );
      break;
    }
    case "make_slides": {
      const it = items[0];
      const done = it ? applied.get(it.key) : undefined;
      body = it ? (
        <div className="space-y-2 text-sm">
          <p className="font-medium">{it.title}</p>
          <ol className="list-decimal space-y-0.5 pl-5 text-xs text-muted-foreground">
            {it.slides.slice(0, 12).map((sl: any, i: number) => (
              <li key={i}>{sl.title}</li>
            ))}
            {it.slides.length > 12 && <li>… {it.slides.length - 12} more</li>}
          </ol>
          {done?.entityId ? (
            <Button size="sm" onClick={() => openNote(done.entityId!, it.title || "Slides", p.workspaceId)}>
              Open the slides
            </Button>
          ) : (
            <>
              {p.error && <p className="text-xs text-destructive">{p.error}</p>}
              <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
              <Button size="sm" disabled={busy} onClick={() => apply([{ key: it.key }])}>
                Create the slides
              </Button>
            </>
          )}
          <p className="text-xs text-muted-foreground">The slides are a note: edit them like any note, then choose Present.</p>
        </div>
      ) : null;
      break;
    }
    case "project_setup": {
      const briefItem = items.find((i) => i.key === "brief");
      const group = (prefix: string) => items.filter((i) => i.key.startsWith(prefix));
      const check = (i: any, label: React.ReactNode, extra?: React.ReactNode) => (
        <li key={i.key} className="flex items-start gap-2 py-1">
          <Checkbox checked={selected.has(i.key)} disabled={applied.has(i.key)} onCheckedChange={() => toggle(i.key)} className="mt-0.5" aria-label={`Include ${typeof label === "string" ? label : i.key}`} />
          <div className="min-w-0 flex-1 space-y-1">
            {label}
            {extra}
            {appliedNote(i.key)}
          </div>
        </li>
      );
      const BRIEF_LABELS: Record<string, string> = { motivation: "Motivation", contribution: "Intended contribution", scope: "Scope", constraints: "Constraints", targetOutputs: "Target outputs", successCriteria: "Success criteria" };
      body = (
        <div className="space-y-4 text-sm">
          {briefItem && (
            <section className="space-y-1">
              <h4 className="font-semibold">Project overview</h4>
              <ul>
                {check(
                  briefItem,
                  <span className="font-medium">Fill the overview with these texts (a new version; earlier ones are kept)</span>,
                  <div className="grid gap-2 sm:grid-cols-2">
                    {Object.entries(BRIEF_LABELS).map(([k, label]) => (
                      <div key={k} className="space-y-0.5">
                        <Label className="text-xs" htmlFor={`brief-${k}`}>
                          {label}
                        </Label>
                        <Textarea id={`brief-${k}`} rows={2} value={String(val(briefItem, k) ?? "")} onChange={(e) => edit(briefItem.key, k, e.target.value)} placeholder="Not in the proposal" />
                      </div>
                    ))}
                  </div>,
                )}
              </ul>
            </section>
          )}
          {group("q:").length > 0 && (
            <section>
              <h4 className="font-semibold">Research questions and hypotheses</h4>
              <ul>
                {group("q:").map((i) =>
                  check(
                    i,
                    <span>
                      <Badge variant="outline" className="mr-1">
                        {i.kind === "hypothesis" ? "Hypothesis" : "Question"}
                      </Badge>
                      <Input aria-label="Text" className="mt-1 h-8" value={String(val(i, "text"))} onChange={(e) => edit(i.key, "text", e.target.value)} />
                    </span>,
                    i.rationale ? <p className="text-xs text-muted-foreground">{i.rationale}</p> : undefined,
                  ),
                )}
              </ul>
            </section>
          )}
          {group("m:").length > 0 && (
            <section>
              <h4 className="font-semibold">Milestones and deliverables</h4>
              <ul>
                {group("m:").map((i) =>
                  check(
                    i,
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{i.kind === "deliverable" ? "Deliverable" : "Milestone"}</Badge>
                      <Input aria-label="Title" className="h-8 min-w-48 flex-1" value={String(val(i, "title"))} onChange={(e) => edit(i.key, "title", e.target.value)} />
                      <Input aria-label="Due date" type="date" className="h-8 w-40" value={String(val(i, "dueDate") ?? "")} onChange={(e) => edit(i.key, "dueDate", e.target.value || null)} />
                    </div>,
                    i.description ? <p className="text-xs text-muted-foreground">{i.description}</p> : undefined,
                  ),
                )}
              </ul>
            </section>
          )}
          {group("r:").length > 0 && (
            <section>
              <h4 className="font-semibold">Suggested reading</h4>
              <p className="text-xs text-muted-foreground">Real papers found in OpenAlex (a free catalogue of research papers) for topics drawn from your proposal. Selected ones go to the library with a “To read” task; open-access PDFs are downloaded.</p>
              <ul>
                {group("r:").map((i) =>
                  check(
                    i,
                    <span className="font-medium">{i.title}</span>,
                    <p className="text-xs text-muted-foreground">
                      {[i.authors?.slice(0, 3).map((a: any) => a.family).join(", "), i.year, i.venue].filter(Boolean).join(" · ")}
                      {i.isOa && <Badge className="ml-1" variant="secondary">Open access</Badge>}
                      <span className="block">For: {i.topic}</span>
                    </p>,
                  ),
                )}
              </ul>
            </section>
          )}
          {group("t:").length > 0 && (
            <section>
              <h4 className="font-semibold">Literature search topics</h4>
              <ul>
                {group("t:").map((i) => check(i, <span className="font-medium">{i.query}</span>, <p className="text-xs text-muted-foreground">{i.why} — adds a “Search the literature” task.</p>))}
              </ul>
            </section>
          )}
          <Button
            size="sm"
            disabled={busy || ![...selected].some((k) => !applied.has(k))}
            onClick={async () => {
              await apply(selItems().filter((x) => !applied.has(x.key)));
              qc.invalidateQueries({ queryKey: ["brief", p.workspaceId] });
              qc.invalidateQueries({ queryKey: ["overview", p.workspaceId] });
              qc.invalidateQueries({ queryKey: ["library", p.workspaceId] });
            }}
          >
            Add the selected items to the project
          </Button>
        </div>
      );
      break;
    }
    case "reading_note": {
      const it = items[0];
      body = (
        <div className="space-y-2 text-sm">
          <p>
            <Badge variant="outline">{(READING_COVERAGE.labels as Record<string, string>)[r.coverage] ?? r.coverage}</Badge> <span className="text-xs">{r.coverageDetail}</span>
          </p>
          {(["question", "method", "data", "limitations"] as const).map((f) => (
            <div key={f} className="space-y-0.5">
              <Label className="text-xs capitalize" htmlFor={`rn-${f}`}>
                {f === "data" ? "Data or setting" : f}
              </Label>
              <Textarea id={`rn-${f}`} rows={2} value={String(val(it, f) ?? "")} placeholder="Not stated in the available text" onChange={(e) => edit(it.key, f, e.target.value || null)} />
            </div>
          ))}
          <div>
            <p className="text-xs font-medium">Reported findings (as the authors report them)</p>
            <ul className="list-disc pl-5">
              {it.findings.map((f: any, i: number) => (
                <li key={i}>
                  {f.text} <WithRefs text={f.pageRefs.length ? `[${f.pageRefs.join(", ")}]` : ""} index={index} />
                </li>
              ))}
              {!it.findings.length && <li className="text-muted-foreground">None found in the available text.</li>}
            </ul>
          </div>
          <div>
            <p className="text-xs font-medium">Quotations (verified against the page text)</p>
            {it.quotes.map((q: any, i: number) => (
              <blockquote key={i} className="border-l-2 pl-2 text-xs italic">
                {q.text} — <WithRefs text={`[${q.pageRef}]`} index={index} />
              </blockquote>
            ))}
          </div>
          {appliedNote(it.key)}
          <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
          <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }])}>
            Save reading note
          </Button>
        </div>
      );
      break;
    }
    case "compare_papers": {
      const it = items[0];
      body = (
        <div className="space-y-2 text-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  {["Paper", "Question", "Method", "Data", "Findings", "Limitations"].map((h) => (
                    <th key={h} className="border px-1 py-0.5 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {it.rows.map((row: any) => (
                  <tr key={row.itemRef}>
                    <td className="border px-1 py-0.5">{row.title}</td>
                    {["question", "method", "data", "findings", "limitations"].map((f) => (
                      <td key={f} className="border px-1 py-0.5 align-top">
                        {row[f] ?? <span className="text-muted-foreground">Unknown</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(
            [
              ["Where they agree", it.agreements],
              ["Where they differ", it.differences],
              ["What it means for the project", it.implications],
            ] as [string, { text: string; refs: string[] }[] | undefined][]
          ).map(([title, points]) =>
            points?.length ? (
              <section key={title} className="space-y-1">
                <h4 className="text-xs font-semibold">{title}</h4>
                <ul className="list-disc space-y-1 pl-4 text-xs">
                  {points.map((x, i) => (
                    <li key={i}>
                      <WithRefs text={x.text} index={index} /> {x.refs.length > 0 && <WithRefs text={`[${x.refs.join(", ")}]`} index={index} />}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null,
          )}
          {it.missing?.length > 0 && (
            <section className="space-y-1">
              <h4 className="text-xs font-semibold">Missing for a fair comparison</h4>
              <ul className="list-disc pl-4 text-xs text-muted-foreground">
                {it.missing.map((x: string, i: number) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </section>
          )}
          {(it.observations ?? []).map((o: string, i: number) => (
            <p key={i} className="text-xs">
              {o}
            </p>
          ))}
          {appliedNote(it.key)}
          <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }])}>
            Save the comparison as a note
          </Button>
        </div>
      );
      break;
    }
    case "ask_project": {
      const it = items[0];
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">Answered only from this project's notes, records and papers, by matching your words. It does not search the wider literature.</p>
          <p className="whitespace-pre-wrap">
            <WithRefs text={it.answer} index={index} />
          </p>
          <p className="text-xs">
            <strong>Support:</strong> {it.sufficient ? "enough in your notes and papers, according to the AI" : "not enough in this project's notes and papers"}
            {it.gaps ? `. ${it.gaps}` : ""}
          </p>
          {it.contradictions.length > 0 && (
            <div className="rounded border p-2 text-xs">
              <p className="font-medium">Evidence against it in your notes and papers</p>
              <ul className="list-disc pl-4">
                {it.contradictions.map((c: any, i: number) => (
                  <li key={i}>
                    {c.description} <WithRefs text={c.refs.length ? `[${c.refs.join(", ")}]` : ""} index={index} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-xs">
            <p className="font-medium">Sources</p>
            <ul className="space-y-0.5">
              {it.citations.map((c: any, i: number) => (
                <li key={i}>
                  <WithRefs text={`[${c.ref}]`} index={index} /> {index[c.ref]?.title}
                  {index[c.ref]?.page ? `, PDF p. ${index[c.ref].page}` : ""}
                  {c.quote && <span className="block italic">“{c.quote}”</span>}
                </li>
              ))}
            </ul>
          </div>
          {appliedNote(it.key)}
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "new_note")}>
              Save answer as note
            </Button>
          </div>
        </div>
      );
      break;
    }
    case "progress_summary": {
      const it = items[0];
      const refIndex = r.refIndex ?? {};
      body = (
        <div className="space-y-2 text-sm">
          <p>{it.overview}</p>
          {(["work", "results", "upcoming", "blockers"] as const).map((sec) => {
            const entries = it.entries.filter((e: any) => e.section === sec);
            return (
              <div key={sec}>
                <p className="text-xs font-medium">{{ work: "Recorded work", results: "Decisions and results", upcoming: "Upcoming", blockers: "Blockers" }[sec]}</p>
                <ul className="list-disc pl-5 text-xs">
                  {entries.map((e: any) => (
                    <li key={e.key}>
                      {e.text} <span className="text-muted-foreground">({e.refs.map((ref: string) => refIndex[ref]?.title ?? ref).join("; ")})</span>
                    </li>
                  ))}
                  {!entries.length && <li className="text-muted-foreground">Nothing recorded.</li>}
                </ul>
              </div>
            );
          })}
          {appliedNote(it.key)}
          <FolderSelect workspaceId={p.workspaceId} value={folderId} onChange={setFolderId} />
          <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }])}>
            Save as progress note
          </Button>
        </div>
      );
      break;
    }
    case "build_outline": {
      body = (
        <div className="space-y-2 text-sm">
          <ul className="space-y-2">
            {items.map((s) => (
              <li key={s.key} className="rounded-md border bg-background p-2">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-2" checked={selected.has(s.key)} disabled={applied.has(s.key)} onChange={() => toggle(s.key)} aria-label={`Include ${s.heading}`} />
                  <span className="w-full space-y-1">
                    <Input className="h-8" value={String(val(s, "heading"))} onChange={(e) => edit(s.key, "heading", e.target.value)} aria-label="Section heading" />
                    {s.exists && <span className="block text-xs">A section with this heading already exists.</span>}
                    <ul className="list-disc pl-5 text-xs">
                      {s.questions.map((q: string, i: number) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                    {s.refs.length > 0 && (
                      <span className="block text-xs">
                        Sources: <WithRefs text={`[${s.refs.join(", ")}]`} index={index} />
                      </span>
                    )}
                  </span>
                </label>
                {appliedNote(s.key)}
              </li>
            ))}
          </ul>
          <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
            Add {selected.size} section(s) to the manuscript
          </Button>
        </div>
      );
      break;
    }
    case "draft_section": {
      const it = items[0];
      body = (
        <div className="space-y-2 text-sm">
          {it.evidenceStates?.length > 0 && (
            <ul className="text-xs">
              {it.evidenceStates.map((s: any) => (
                <li key={s.ref}>
                  {s.ref} “{s.title}”: recorded as <strong>{s.label}</strong>
                </li>
              ))}
            </ul>
          )}
          <Label htmlFor="draft-md">Draft (you can edit it; source numbers such as [S1] become links)</Label>
          <Textarea id="draft-md" rows={10} value={String(val(it, "draftMarkdown"))} onChange={(e) => edit(it.key, "draftMarkdown", e.target.value)} className="font-mono text-xs" />
          <details>
            <summary className="cursor-pointer text-xs">Preview</summary>
            <MarkdownPreview markdown={String(val(it, "draftMarkdown"))} className="text-sm" />
          </details>
          {it.placeholders?.length > 0 && <p className="text-xs">Placeholders to fill in: {it.placeholders.join("; ")}</p>}
          {appliedNote(it.key)}
          <Button size="sm" disabled={busy || applied.has(it.key)} onClick={() => apply([{ key: it.key }], "insert")}>
            Insert draft at the end of the section
          </Button>
        </div>
      );
      break;
    }
    case "review_draft": {
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">{r.disclaimer}</p>
          {!items.length && <p className="text-muted-foreground">No issues reported.</p>}
          <ul className="space-y-2">
            {items.map((iss) => (
              <li key={iss.key} className="rounded-md border bg-background p-2">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-1" checked={selected.has(iss.key)} disabled={applied.has(iss.key)} onChange={() => toggle(iss.key)} />
                  <span>
                    <span className="text-xs font-semibold">{ISSUE_TYPES[iss.type] ?? iss.type.replace(/_/g, " ")}</span>
                    {iss.line && <span className="text-xs text-muted-foreground"> · line {iss.line}</span>}
                    <blockquote className="border-l-2 pl-2 text-xs italic">{iss.quote}</blockquote>
                    <span className="block">{iss.description}</span>
                    {iss.suggestion && <span className="block text-xs">Suggestion: {iss.suggestion}</span>}
                    {!iss.located && <span className="block text-xs">Location not found in the draft.</span>}
                  </span>
                </label>
                {appliedNote(iss.key)}
              </li>
            ))}
          </ul>
          {items.length > 0 && (
            <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
              Add {selected.size} selected as comments
            </Button>
          )}
        </div>
      );
      break;
    }
    case "check_requirements": {
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">{r.disclaimer}</p>
          <ul className="space-y-1">
            {items.map((it) => (
              <li key={it.key} className="rounded border bg-background p-2 text-xs">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" checked={selected.has(it.key)} onChange={() => toggle(it.key)} />
                  <span>
                    <strong>{REQUIREMENT_STATUS[it.status] ?? it.status}:</strong> {it.requirement}
                    <span className="block text-muted-foreground">{it.basis}</span>
                    <span className="block italic">“{it.guidelineQuote}”{!it.quoteFound && " (not found in the pasted guidelines)"}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
            Save selected as a checklist note
          </Button>
        </div>
      );
      break;
    }
    case "plan_revisions": {
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">Proposed changes are plans. Nothing is marked as changed until you confirm it in the revision table.</p>
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.key} className="space-y-1 rounded-md border bg-background p-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.has(it.key)} disabled={applied.has(it.key)} onChange={() => toggle(it.key)} />
                  <strong>{it.itemNumber}</strong> {it.sectionTitle && <span className="text-xs">→ {it.sectionTitle}</span>}
                </label>
                <Textarea rows={2} value={String(val(it, "proposedChange"))} onChange={(e) => edit(it.key, "proposedChange", e.target.value)} aria-label={`Proposed change for ${it.itemNumber}`} />
                <Input className="h-8" value={String(val(it, "taskTitle"))} onChange={(e) => edit(it.key, "taskTitle", e.target.value)} aria-label="Task title" />
                <p className="text-xs text-muted-foreground">{it.hasTask ? "A revision task already exists; it will be kept." : "A linked revision task will be created."}</p>
                {appliedNote(it.key)}
              </li>
            ))}
          </ul>
          <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
            Apply {selected.size} selected plan(s)
          </Button>
        </div>
      );
      break;
    }
    case "response_to_reviewers": {
      body = (
        <div className="space-y-2 text-sm">
          <p className="text-xs text-muted-foreground">Responses are saved as drafts in the revision table. Nothing is sent. Open requests cannot be described as changed.</p>
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.key} className="space-y-1 rounded-md border bg-background p-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.has(it.key)} disabled={applied.has(it.key)} onChange={() => toggle(it.key)} />
                  <strong>{it.itemNumber}</strong> <span className="text-xs">({(REVISION_CHANGE_STATUS.labels as Record<string, string>)[it.status] ?? it.status})</span>
                </label>
                {it.claimFlag && <p className="text-xs font-medium">This draft claimed a change that is not confirmed. Edit it before use.</p>}
                <Textarea rows={3} value={String(val(it, "response"))} onChange={(e) => edit(it.key, "response", e.target.value)} aria-label={`Response for ${it.itemNumber}`} />
                {appliedNote(it.key)}
              </li>
            ))}
          </ul>
          <Button size="sm" disabled={busy || !selected.size} onClick={() => apply(selItems())}>
            Use {selected.size} selected response(s) as drafts
          </Button>
        </div>
      );
      break;
    }
    default: {
      // Results of the manuscript and email actions are reviewed where they belong.
      const threadId: string | undefined = p.action.startsWith("mail_") ? p.params?.threadId : undefined;
      body = (
        <div className="space-y-2 text-sm">
          <p>
            {p.action.startsWith("mail_")
              ? "This result is shown in Correspondence, with the email conversation."
              : p.targetManuscriptId
                ? "This result is shown in the manuscript."
                : "This result cannot be shown here."}
          </p>
          {p.action.startsWith("mail_") && (
            <Button size="sm" variant="outline" onClick={() => openView("correspondence", p.workspaceId, "Correspondence", threadId ? { thread: threadId } : undefined)}>
              Open the conversation
            </Button>
          )}
        </div>
      );
    }
  }

  return (
    <div className="space-y-3">
      {header}
      {staleBox}
      {body}
      {p.targetManuscriptId && (
        <Button size="xs" variant="link" className="px-0" onClick={() => openView("manuscript", p.workspaceId, "Manuscript", undefined, p.targetManuscriptId!)}>
          Open the manuscript
        </Button>
      )}
      <Feedback proposalId={p.id} />
    </div>
  );
}

type Rating = 1 | -1;

/** "Was this useful?": a yes or no and an optional comment, to know which results to improve. */
function Feedback({ proposalId }: { proposalId: string }) {
  const key = ["proposal", proposalId, "feedback"];
  const q = useQuery({ queryKey: key, queryFn: () => get<{ feedback: { rating: Rating; comment: string | null } | null }>(`/api/ai/proposals/${proposalId}/feedback`) });
  const qc = useQueryClient();
  const saved = q.data?.feedback ?? null;
  const [comment, setComment] = useState("");
  const [writing, setWriting] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setComment(saved?.comment ?? ""), [saved?.comment]);
  if (!q.isSuccess) return null;
  const send = async (rating: Rating, text: string | null) => {
    setBusy(true);
    try {
      const res = await put<{ feedback: { rating: Rating; comment: string | null } }>(`/api/ai/proposals/${proposalId}/feedback`, { rating, comment: text });
      qc.setQueryData(key, res);
      return true;
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const rate = async (rating: Rating) => {
    if (await send(rating, saved?.comment ?? null)) setWriting(!saved?.comment);
  };
  return (
    <section aria-label="Was this useful?" className="space-y-2 border-t pt-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">{saved ? "Thank you for your answer." : "Was this useful?"}</span>
        {([1, -1] as const).map((r) => (
          <Button key={r} size="xs" variant={saved?.rating === r ? "default" : "outline"} aria-pressed={saved?.rating === r} disabled={busy} onClick={() => void rate(r)}>
            {r === 1 ? <ThumbsUp aria-hidden /> : <ThumbsDown aria-hidden />} {r === 1 ? "Yes" : "No"}
          </Button>
        ))}
        {saved && !writing && (
          <Button size="xs" variant="link" className="px-0" onClick={() => setWriting(true)}>
            {saved.comment ? "Change your comment" : "Add a comment"}
          </Button>
        )}
      </div>
      {saved && writing && (
        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            void send(saved.rating, comment.trim() || null).then((ok) => {
              if (!ok) return;
              setWriting(false);
              toast.success("Thank you: this helps us improve BrainLog.");
            });
          }}
        >
          <Label htmlFor={`feedback-${proposalId}`} className="text-xs">
            {saved.rating === 1 ? "What was most useful? (optional)" : "What was wrong or missing? (optional)"}
          </Label>
          <Textarea id={`feedback-${proposalId}`} rows={2} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
          <p className="text-xs text-muted-foreground">BrainLog's team reads your answer and comment, never the result itself or your notes.</p>
          <div className="flex gap-2">
            <Button size="xs" type="submit" disabled={busy || !comment.trim()}>
              Send
            </Button>
            <Button size="xs" type="button" variant="ghost" onClick={() => setWriting(false)}>
              Not now
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

